import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import { asLockConflict } from '@/lib/locks/api';
import type {
  CancelRequest,
  GenerationRunAccepted,
  GroupFlightOffersQuery,
  GroupFlightOffersView,
  OverrideRequest,
  PassengerAge,
  PassengerAgesResult,
  RebookingConflict,
  RebookingGroup,
  TicketResult,
  RebookingOptionsView,
  RejectRequest,
  SelectRequest,
} from './types';
import { newIdempotencyKey } from '@/lib/uuid';

function optionsPath(caseUrn: string): string {
  return `v1/cases/${encodeURIComponent(caseUrn)}/rebooking/options`;
}

function groupPath(groupId: string, verb: string): string {
  return `v1/groups/${encodeURIComponent(groupId)}/rebooking/${verb}`;
}

// Every write carries an Idempotency-Key (§13). One key per logical attempt.
// The group routes carry no caseUrn, so the caller passes it as `X-Case-Urn`
// purely so the BFF can fan out a CaseChanged WS event to the case room (live
// cross-user updates); the rebooking domain never sees the header.
function writeHeaders(caseUrn?: string): Record<string, string> {
  return {
    'Idempotency-Key': newIdempotencyKey(),
    ...(caseUrn ? { 'X-Case-Urn': caseUrn } : {}),
  };
}

/**
 * §10 guard errors surface as 409 with `{ error:'conflict', code, message }`.
 * Thrown so the panel can render the message inline and decide not to retry.
 */
export class RebookingConflictError extends Error {
  readonly conflict: RebookingConflict;
  constructor(conflict: RebookingConflict) {
    super(conflict.message);
    this.name = 'RebookingConflictError';
    this.conflict = conflict;
  }
}

export function isRebookingConflictError(e: unknown): e is RebookingConflictError {
  return e instanceof RebookingConflictError;
}

async function asConflict(error: unknown): Promise<never> {
  if (error instanceof HTTPError && error.response.status === 409) {
    try {
      const body = (await error.response.clone().json()) as { error?: string; code?: string; message?: string };
      // Two distinct 409s on a rebooking write:
      //   • lock contention (auto-lock on write-intent) → { error: 'locked', … }
      //     → LockConflictError ("PNR x is being edited by <holder>").
      //   • §10 domain guard → { error: 'conflict', code, message } → RebookingConflictError.
      if (body?.error === 'locked') {
        await asLockConflict(error); // throws LockConflictError
      }
      if (body?.error === 'conflict' && body.code) {
        throw new RebookingConflictError(body as RebookingConflict);
      }
    } catch (parseErr) {
      if (parseErr instanceof RebookingConflictError) throw parseErr;
      if (parseErr instanceof Error && parseErr.name === 'LockConflictError') throw parseErr;
      // fall through to rethrow original
    }
  }
  throw error;
}

export async function getRebookingOptions(caseUrn: string): Promise<RebookingOptionsView> {
  return http.get(optionsPath(caseUrn)).json<RebookingOptionsView>();
}

/**
 * Pre-run policy gate (rebooking TDS §18 "opt-in by absence"): whether the
 * rebooking engine runs at all for a station/leg/cabin. `enabled: false` means
 * a generation could only end POLICY_DISABLED — the UI withholds the rebook
 * actions and the flow continues with hotels/care. `policyUrn` names the
 * governing (disabling) policy for the "view policy" link.
 */
export interface RebookingAvailability {
  enabled: boolean;
  policyUrn?: string;
  matchType?: string;
}

export async function getRebookingAvailability(params: {
  airport?: string | undefined;
  leg?: string | undefined;
  cabin?: string | undefined;
}): Promise<RebookingAvailability> {
  const qs = new URLSearchParams();
  if (params.airport) qs.set('airport', params.airport);
  if (params.leg) qs.set('leg', params.leg);
  if (params.cabin) qs.set('cabin', params.cabin);
  return http.get(`v1/rebooking/availability?${qs.toString()}`).json<RebookingAvailability>();
}

export async function triggerGeneration(caseUrn: string): Promise<GenerationRunAccepted> {
  // The options route already carries the caseUrn in the path, so no X-Case-Urn.
  return http
    .post(optionsPath(caseUrn), { headers: writeHeaders() })
    .json<GenerationRunAccepted>();
}

/** Generate options for ONE PNR group (sub-case) WITHOUT running the disruption-wide
 *  bulk — the operator resolves a single passenger's problem in place. Targets the
 *  case's options route with a `groupId` body (rebooking `TriggerOptionsDto.groupId`
 *  → single-group generation). */
export async function triggerGroupGeneration(
  caseUrn: string,
  groupId: string,
  /** POL-16 — supervisor-forced generation past a disabling policy. The BFF
   *  gates the role (OPS_SUPERVISOR) and requires the reason; the domain stamps
   *  the provenance on its evaluation trace. */
  force?: { reason: string },
): Promise<GenerationRunAccepted> {
  try {
    return await http
      .post(optionsPath(caseUrn), {
        json: { groupId, ...(force ? { force } : {}) },
        headers: writeHeaders(),
      })
      .json<GenerationRunAccepted>();
  } catch (error) {
    // The targeted generate carries the same §10/§14 domain guards as the other
    // group writes (ALREADY_TICKETED) — surface them as RebookingConflictError.
    return asConflict(error);
  }
}

/**
 * «Cargar edades» — the ages of a PNR group's children / infants (Duffel and Atlas quote a CHD /
 * INF only with its exact age). The domain validates them, saves them (audited with the
 * operator) and regenerates that group's options; a refusal comes back as a 400 with its code
 * (`PASSENGER_AGE_TYPE_MISMATCH`, …) or a §10 409.
 */
export async function setPassengerAges(
  caseUrn: string,
  groupId: string,
  ages: PassengerAge[],
): Promise<PassengerAgesResult> {
  try {
    return await http
      .post(groupPath(groupId, 'passenger-ages'), { json: { ages }, headers: writeHeaders(caseUrn) })
      .json<PassengerAgesResult>();
  } catch (error) {
    return asConflict(error);
  }
}

export async function selectOption(
  groupId: string,
  body: SelectRequest,
  caseUrn?: string,
): Promise<RebookingGroup> {
  try {
    return await http
      .post(groupPath(groupId, 'select'), { json: body, headers: writeHeaders(caseUrn) })
      .json<RebookingGroup>();
  } catch (error) {
    return asConflict(error);
  }
}

/** Operator-gated mock PSS ticketing (rebooking-engine S5). Issues the ticket for
 *  a SELECTED group; idempotent on the domain. Never auto-called — only the
 *  operator's explicit "Issue ticket" action reaches here. */
export async function issueTicket(groupId: string, caseUrn?: string): Promise<TicketResult> {
  return await http
    .post(groupPath(groupId, 'issue-ticket'), { json: {}, headers: writeHeaders(caseUrn) })
    .json<TicketResult>();
}

/** Case-wide ticketing — the overview's "Emitir tickets": every SELECTED group of
 *  the case is committed to a ticket in one explicit operator action (the airline
 *  re-protects the flight, not one PNR at a time). Idempotent on the domain. */
export interface CaseTicketsResult {
  caseUrn: string;
  total: number;
  issued: number;
  alreadyTicketed: number;
  skipped: number;
  failed: number;
  skippedGroupIds: string[];
}
export async function issueTicketsForCase(caseUrn: string): Promise<CaseTicketsResult> {
  return await http
    .post(`v1/cases/${encodeURIComponent(caseUrn)}/rebooking/issue-tickets`, {
      json: {},
      headers: writeHeaders(caseUrn),
    })
    .json<CaseTicketsResult>();
}

export async function rejectOption(
  groupId: string,
  body: RejectRequest,
  caseUrn?: string,
): Promise<RebookingGroup> {
  try {
    return await http
      .post(groupPath(groupId, 'reject'), { json: body, headers: writeHeaders(caseUrn) })
      .json<RebookingGroup>();
  } catch (error) {
    return asConflict(error);
  }
}

export async function cancelSelection(
  groupId: string,
  body: CancelRequest,
  caseUrn?: string,
): Promise<RebookingGroup> {
  try {
    return await http
      .post(groupPath(groupId, 'cancel'), { json: body, headers: writeHeaders(caseUrn) })
      .json<RebookingGroup>();
  } catch (error) {
    return asConflict(error);
  }
}

/**
 * A refused picker search, with the domain's machine `code` when it gave one — e.g. 422
 * `ROUTE_REQUIRED` (the case has no route: the operator gives origin and destination), as
 * opposed to the providers being unreachable.
 */
export class FlightOffersSearchError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, code?: string, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'FlightOffersSearchError';
    this.status = status;
    this.code = code;
  }
}

/**
 * The manual picker's offers for ONE PNR group (`groupId` = sub-case URN), from the flight
 * providers — never the schedule. A POST (the domain keeps what it listed so the pick can
 * send `offerRef` back) but a read for the operator: no Idempotency-Key, no lock. The
 * caseUrn rides as `X-Case-Urn` so the domain can resolve a group it has not seen yet.
 * Cabin and party are sent only when the console knows better than the case: left out,
 * the domain asks for the group's own cabin and pax count — what generation asks.
 */
export async function searchGroupFlightOffers(
  groupId: string,
  query: GroupFlightOffersQuery,
  caseUrn?: string,
): Promise<GroupFlightOffersView> {
  const json: Record<string, unknown> = {};
  if (query.origin) json.origin = query.origin.toUpperCase();
  if (query.destination) json.destination = query.destination.toUpperCase();
  if (query.date) json.date = query.date;
  if (query.cabin) json.cabin = query.cabin;
  if (query.seats) json.seats = query.seats;
  try {
    return await http
      .post(groupPath(groupId, 'flight-offers'), {
        json,
        headers: caseUrn ? { 'X-Case-Urn': caseUrn } : {},
        // Providers are asked one after the other (the airline's own service, then Duffel…).
        timeout: 60_000,
      })
      .json<GroupFlightOffersView>();
  } catch (error) {
    if (!(error instanceof HTTPError)) throw error;
    let body: { code?: string; message?: string } = {};
    try {
      body = ((await error.response.clone().json()) ?? {}) as typeof body;
    } catch {
      /* no JSON body */
    }
    throw new FlightOffersSearchError(error.response.status, body.code, body.message);
  }
}

export async function overrideSelection(
  groupId: string,
  body: OverrideRequest,
  caseUrn?: string,
): Promise<RebookingGroup> {
  try {
    return await http
      .post(groupPath(groupId, 'override'), { json: body, headers: writeHeaders(caseUrn) })
      .json<RebookingGroup>();
  } catch (error) {
    return asConflict(error);
  }
}
